import { useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import {
  Alert,
  Button,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { v4 as uuidv4 } from "uuid";
import {
  generatedApi,
  useGetClarkProfileQuery,
  useUpdateClarkProfileMutation,
} from "store/api/generatedApi";
import { GetClarkProfileResponse } from "store/api/generatedZodApi/auth";
import { fetchErrorDetail, isFetchBaseQueryError } from "helpers";
import { useTenantTranslation } from "hooks/formatters";

interface IdentifierRow {
  key: string;
  identifier: string;
}

export interface ClarkProfileFields {
  name: string;
  description: string;
  identifierRows: IdentifierRow[];
}

const validationErrorMessage = (error: unknown): string | null => {
  if (
    isFetchBaseQueryError(error) &&
    error.status === 422 &&
    typeof error.data === "object" &&
    error.data !== null &&
    "detail" in error.data &&
    Array.isArray(error.data.detail)
  ) {
    const firstError: unknown = error.data.detail[0];
    if (
      typeof firstError === "object" &&
      firstError !== null &&
      "msg" in firstError &&
      typeof firstError.msg === "string"
    ) {
      return firstError.msg;
    }
  }
  return null;
};

export const EMPTY_CLARK_PROFILE_FIELDS: ClarkProfileFields = {
  name: "",
  description: "",
  identifierRows: [],
};

const PHONE_NUMBER_FORMATTING = /[\s\-.()]/g;
const E164_PHONE_NUMBER = /^\+[1-9]\d{6,14}$/;

const normaliseImessageIdentifier = (identifier: string) =>
  identifier.includes("@")
    ? identifier.trim().toLowerCase()
    : identifier.replace(PHONE_NUMBER_FORMATTING, "");

const isInvalidImessageIdentifier = (identifier: string) =>
  identifier.trim() !== "" &&
  !identifier.includes("@") &&
  !E164_PHONE_NUMBER.test(normaliseImessageIdentifier(identifier));

export const isClarkProfileComplete = (fields: ClarkProfileFields) =>
  fields.name.trim() !== "" &&
  fields.description.trim() !== "" &&
  !fields.identifierRows.some((row) =>
    isInvalidImessageIdentifier(row.identifier)
  );

export const useSaveClarkProfile = ({ tenant }: { tenant: string }) => {
  const { t } = useTenantTranslation();
  const [getClarkProfile] = generatedApi.useLazyGetClarkProfileQuery();
  const [updateClarkProfile, { isLoading: isSaving }] =
    useUpdateClarkProfileMutation();
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});

  const clearSaveError = (email: string) => {
    setSaveErrors((errors) =>
      Object.fromEntries(
        Object.entries(errors).filter(
          ([erroredEmail]) => erroredEmail !== email
        )
      )
    );
  };

  const recordSaveError = ({
    email,
    error,
  }: {
    email: string;
    error: unknown;
  }) => {
    const saveError =
      fetchErrorDetail(error) ??
      validationErrorMessage(error) ??
      t("couldntSaveClarkProfile");
    setSaveErrors((errors) => ({ ...errors, [email]: saveError }));
  };

  const putProfile = async ({
    email,
    fields,
  }: {
    email: string;
    fields: ClarkProfileFields;
  }) => {
    await updateClarkProfile({
      tenantName: tenant,
      clarkProfileUpdate: {
        email,
        name: fields.name,
        description: fields.description,
        handles: fields.identifierRows
          .map((row) => row.identifier.trim())
          .filter((identifier) => identifier !== "")
          .map((identifier) => ({ channel: "imessage", identifier })),
      },
    }).unwrap();
  };

  const save = async ({
    email,
    fields,
  }: {
    email: string;
    fields: ClarkProfileFields;
  }) => {
    clearSaveError(email);
    try {
      await putProfile({ email, fields });
    } catch (error) {
      recordSaveError({ email, error });
      throw error;
    }
  };

  const saveKeepingExistingHandles = async ({
    email,
    fields,
  }: {
    email: string;
    fields: ClarkProfileFields;
  }) => {
    clearSaveError(email);
    try {
      const existingProfile = GetClarkProfileResponse.parse(
        await getClarkProfile({ tenantName: tenant, email }).unwrap()
      );
      const existingIdentifiers = new Set(
        existingProfile.handles.map((handle) =>
          normaliseImessageIdentifier(handle.identifier)
        )
      );
      await putProfile({
        email,
        fields: {
          ...fields,
          identifierRows: [
            ...existingProfile.handles.map((handle) => ({
              key: handle.identifier,
              identifier: handle.identifier,
            })),
            ...fields.identifierRows.filter(
              (row) =>
                !existingIdentifiers.has(
                  normaliseImessageIdentifier(row.identifier)
                )
            ),
          ],
        },
      });
    } catch (error) {
      recordSaveError({ email, error });
      throw error;
    }
  };

  const clearSaveErrors = () => {
    setSaveErrors({});
  };

  return {
    isSaving,
    saveErrors,
    clearSaveErrors,
    save,
    saveKeepingExistingHandles,
  };
};

export const useClarkProfileDraft = ({
  tenant,
  email,
  fallbackName,
  grantsClarkUser,
}: {
  tenant: string;
  email: string | null;
  fallbackName: string;
  grantsClarkUser: boolean;
}) => {
  const { data: response, isError: isLoadError } = useGetClarkProfileQuery(
    email === null ? skipToken : { tenantName: tenant, email }
  );
  const parsedProfile =
    response === undefined
      ? undefined
      : GetClarkProfileResponse.safeParse(response);
  const profile = parsedProfile?.success ? parsedProfile.data : undefined;
  const isError = isLoadError || parsedProfile?.success === false;
  const {
    isSaving,
    saveErrors,
    save: saveProfile,
  } = useSaveClarkProfile({
    tenant,
  });
  const [draft, setDraft] = useState<ClarkProfileFields | null>(null);

  const fields: ClarkProfileFields = draft ?? {
    name: profile?.user?.name ?? fallbackName,
    description: profile?.description ?? "",
    identifierRows:
      profile?.handles.map((handle) => ({
        key: handle.identifier,
        identifier: handle.identifier,
      })) ?? [],
  };
  const hasProfile = profile !== undefined && profile.description !== null;
  const isSaveDue =
    profile !== undefined &&
    (draft !== null || (grantsClarkUser && !hasProfile));

  const updateFields = (update: Partial<ClarkProfileFields>) => {
    setDraft({ ...fields, ...update });
  };

  const save = async () => {
    if (email === null) {
      return;
    }
    await saveProfile({ email, fields });
  };

  return {
    isLoaded: profile !== undefined,
    isError,
    fields,
    updateFields,
    isSaveDue,
    canSave:
      (profile !== undefined || !grantsClarkUser) &&
      (!isSaveDue || isClarkProfileComplete(fields)),
    isSaving,
    saveError: email === null ? null : (saveErrors[email] ?? null),
    save,
  };
};

export type ClarkProfileDraft = ReturnType<typeof useClarkProfileDraft>;

const ChannelLabel = ({ children }: { children: string }) => (
  <Typography variant="caption" color="text.secondary">
    {children}
  </Typography>
);

export const ClarkProfileForm = ({
  fields,
  updateFields,
  email,
  showsMissingFields,
  saveError,
  disabled,
}: {
  fields: ClarkProfileFields;
  updateFields: (update: Partial<ClarkProfileFields>) => void;
  email: string;
  showsMissingFields: boolean;
  saveError: string | null;
  disabled: boolean;
}) => {
  const { t } = useTenantTranslation();
  return (
    <Stack gap={1.5}>
      <TextField
        disabled={disabled}
        size="small"
        label={t("name")}
        slotProps={{ inputLabel: { shrink: true } }}
        value={fields.name}
        required
        error={showsMissingFields && fields.name.trim() === ""}
        onChange={(event) => {
          updateFields({ name: event.target.value });
        }}
      />
      <TextField
        disabled={disabled}
        size="small"
        label={t("description")}
        slotProps={{ inputLabel: { shrink: true } }}
        value={fields.description}
        required
        error={showsMissingFields && fields.description.trim() === ""}
        multiline
        minRows={2}
        onChange={(event) => {
          updateFields({ description: event.target.value });
        }}
      />
      <Typography variant="body2">{t("clarkChannels")}</Typography>
      <Stack
        gap={1.5}
        divider={<Divider flexItem />}
        sx={{
          p: 1.5,
          borderRadius: 0.5,
          border: "1px solid",
          borderColor: "grey.300",
        }}
      >
        <Stack>
          <ChannelLabel>{t("email")}</ChannelLabel>
          <Typography variant="body2">{email}</Typography>
        </Stack>
        <Stack gap={1}>
          <ChannelLabel>{t("clarkImessage")}</ChannelLabel>
          {fields.identifierRows.map((row) => (
            <Stack
              key={row.key}
              direction="row"
              gap={1}
              alignItems="flex-start"
            >
              <TextField
                disabled={disabled}
                size="small"
                fullWidth
                value={row.identifier}
                error={isInvalidImessageIdentifier(row.identifier)}
                helperText={t("clarkHandleHelp")}
                slotProps={{
                  htmlInput: { "aria-label": t("clarkImessage") },
                  formHelperText: { sx: { mx: 0 } },
                }}
                onChange={(event) => {
                  updateFields({
                    identifierRows: fields.identifierRows.map((current) =>
                      current.key === row.key
                        ? { ...current, identifier: event.target.value }
                        : current
                    ),
                  });
                }}
              />
              <IconButton
                disabled={disabled}
                size="small"
                aria-label={t("delete")}
                sx={{ mt: 0.5 }}
                onClick={() => {
                  updateFields({
                    identifierRows: fields.identifierRows.filter(
                      (current) => current.key !== row.key
                    ),
                  });
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button
            disabled={disabled}
            size="small"
            startIcon={<AddIcon />}
            sx={{ alignSelf: "flex-start" }}
            onClick={() => {
              updateFields({
                identifierRows: [
                  ...fields.identifierRows,
                  { key: uuidv4(), identifier: "" },
                ],
              });
            }}
          >
            {t("addClarkIdentifier")}
          </Button>
        </Stack>
      </Stack>
      {saveError === null ? null : <Alert severity="error">{saveError}</Alert>}
    </Stack>
  );
};

export const ClarkProfileSection = ({
  draft,
  email,
}: {
  draft: ClarkProfileDraft;
  email: string;
}) => {
  const { t } = useTenantTranslation();

  const content = () => {
    if (draft.isError) {
      return <Alert severity="error">{t("couldntLoadClarkProfile")}</Alert>;
    }
    if (!draft.isLoaded) {
      return <Skeleton variant="rounded" height={160} />;
    }
    return (
      <ClarkProfileForm
        fields={draft.fields}
        updateFields={draft.updateFields}
        email={email}
        showsMissingFields={draft.isSaveDue}
        saveError={draft.saveError}
        disabled={draft.isSaving}
      />
    );
  };

  return (
    <Stack gap={1}>
      <Divider sx={{ mb: 1 }} />
      <Typography variant="subtitle1">{t("clarkProfile")}</Typography>
      {content()}
    </Stack>
  );
};
